import { useGetList } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { Inbox, Mail, PenSquare, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { EmailComposeSheet } from "../contacts/EmailComposeSheet";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Contact } from "../types";
import { useInboxActivity } from "./useInboxActivity";

const IS_DEMO = import.meta.env.VITE_IS_DEMO === "true";

/**
 * Inbox - your mailbox inside Obvious. At deployment the two-way sync
 * (Google Workspace / Microsoft 365 via Nylas) mirrors your real mailbox
 * here and auto-logs every message onto the matching contact's timeline.
 * The demo shows a sample synced inbox plus everything you actually "send"
 * from contact records this session. Real mail (both directions) comes from
 * the event spine via useInboxActivity - see that file for why "sent" used
 * to live in browser localStorage instead of the database.
 */

export const InboxPage = () => {
  const { moduleFlags } = useConfigurationContext();
  const { data: contacts = [] } = useGetList<Contact>("contacts", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "last_seen", order: "DESC" },
  });

  const [query, setQuery] = useState("");
  const [folder, setFolder] = useState<"all" | "in" | "out">("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [composeContact, setComposeContact] = useState<Contact | null>(null);
  const [composeOpen, setComposeOpen] = useState(false);

  const rows = useInboxActivity(contacts);

  if (moduleFlags?.inbox === false) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <p className="text-sm text-muted-foreground">
          Inbox isn't turned on for this workspace yet.
        </p>
      </div>
    );
  }

  const filtered = rows.filter(
    (r) =>
      (folder === "all" || r.direction === folder) &&
      (r.subject.toLowerCase().includes(query.toLowerCase()) ||
        r.contactName.toLowerCase().includes(query.toLowerCase())),
  );
  const selected = filtered.find((r) => r.id === selectedId) ?? filtered[0];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Inbox className="w-5 h-5 text-muted-foreground" />
        <h1 className="text-xl font-semibold flex-1">Inbox</h1>
        <Button
          size="sm"
          onClick={() => {
            setComposeContact(contacts[0] ?? null);
            setComposeOpen(true);
          }}
          disabled={contacts.length === 0}
        >
          <PenSquare className="w-4 h-4" /> New email
        </Button>
      </div>
      <p className="text-sm text-muted-foreground -mt-2 max-w-3xl">
        {IS_DEMO
          ? "Sample synced mailbox - connect Google Workspace or Microsoft 365 under Integrations and the live two-way sync mirrors your real mail here at deployment, auto-logging every message to the matching contact's timeline. Emails you send from contact records this session appear below."
          : "Emails you send from Obvious appear here and log to the matching contact's timeline. Two-way mailbox sync (Google Workspace / Microsoft 365) arrives with the inbox-sync release - once connected, your real mail mirrors here automatically."}
      </p>

      <div className="grid lg:grid-cols-[360px_minmax(0,1fr)] gap-4 items-start">
        {/* List */}
        <Card>
          <CardContent className="p-3 flex flex-col gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-muted-foreground" />
              <Input
                className="h-8 pl-7 text-sm"
                placeholder="Search mail…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="flex gap-1">
              {(
                [
                  { id: "all", label: "All" },
                  { id: "in", label: "Received" },
                  { id: "out", label: "Sent" },
                ] as const
              ).map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFolder(f.id)}
                  className={`rounded-md border px-2 py-0.5 text-xs transition-colors ${
                    folder === f.id
                      ? "border-primary bg-primary/10 font-medium"
                      : "border-border text-muted-foreground hover:border-primary/50"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex flex-col max-h-[520px] overflow-y-auto -mx-1">
              {filtered.length === 0 && (
                <p className="text-sm text-muted-foreground px-1 py-3">
                  Nothing here yet.
                </p>
              )}
              {filtered.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setSelectedId(r.id)}
                  className={`text-left px-2 py-2 rounded-md transition-colors border-b border-border last:border-b-0 ${
                    selected?.id === r.id ? "bg-primary/5" : "hover:bg-muted/50"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {r.unread && (
                      <span className="w-1.5 h-1.5 rounded-full bg-[#2e6ba9] shrink-0" />
                    )}
                    <span
                      className={`text-sm truncate flex-1 ${r.unread ? "font-semibold" : ""}`}
                    >
                      {r.direction === "out" ? `To: ${r.contactName}` : r.contactName}
                    </span>
                    <span className="text-[10px] text-muted-foreground shrink-0">
                      {new Date(r.date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                    </span>
                  </div>
                  <p className={`text-xs truncate ${r.unread ? "font-medium" : "text-muted-foreground"}`}>
                    {r.subject}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {r.body.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ")}
                  </p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Reading pane */}
        <Card>
          <CardContent className="p-5">
            {selected ? (
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <h2 className="font-semibold">{selected.subject}</h2>
                    <p className="text-xs text-muted-foreground">
                      {selected.direction === "out" ? "To" : "From"}:{" "}
                      {selected.contactName}
                      {selected.address ? ` <${selected.address}>` : ""} ·{" "}
                      {new Date(selected.date).toLocaleString()}
                    </p>
                  </div>
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#8fb586]/15 text-[#8fb586] font-medium inline-flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Logged to timeline
                  </span>
                </div>
                {selected.bodyHtml ? (
                  // Demo preview only - real rows never carry HTML, only a
                  // plain-text preview (see useInboxActivity).
                  <div
                    className="text-sm leading-6 border-t border-border pt-3 [&_a]:underline [&_a]:text-[#2e6ba9] [&_ul]:list-disc [&_ul]:pl-5 [&_img]:max-w-full [&_img]:rounded"
                    dangerouslySetInnerHTML={{ __html: selected.bodyHtml }}
                  />
                ) : (
                  <p className="text-sm leading-6 whitespace-pre-line border-t border-border pt-3">
                    {selected.body}
                  </p>
                )}
                <div className="flex items-center gap-3 border-t border-border pt-3">
                  {selected.contactId != null && (
                    <>
                      <Link
                        to={`/contacts/${selected.contactId}/show`}
                        className="text-sm underline hover:no-underline"
                      >
                        Open contact
                      </Link>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const c = contacts.find(
                            (x) => String(x.id) === String(selected.contactId),
                          );
                          if (c) {
                            setComposeContact(c);
                            setComposeOpen(true);
                          }
                        }}
                      >
                        <PenSquare className="w-3.5 h-3.5" /> Reply
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Select an email to read it.
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {composeContact && (
        <EmailComposeSheet
          contact={composeContact}
          open={composeOpen}
          onOpenChange={setComposeOpen}
        />
      )}
    </div>
  );
};

InboxPage.path = "/inbox";
